import { useQuery } from '@tanstack/react-query';
import type { AiStatusDto, AiStreamEvent } from '@fyr/shared';
import { api } from './api';

/** §32 M16 is AI on for me, and which model answers (re-checked now and then; the admin may switch it). */
export function useAiStatus() {
  return useQuery({
    queryKey: ['ai-status'],
    queryFn: () => api.get<AiStatusDto>('/ai/status'),
    staleTime: 60_000,
  });
}

/** §32 M16.2 what "Draft with AI" makes. */
export type DraftKind = 'document' | 'deck';

/** Why a request didn't start (AI off, no access, no text…) — the api's own message. */
export class AiRefused extends Error {}

/**
 * POST and read the answer as it streams (server-sent events). Errors before the stream starts come back as JSON
 * (thrown as AiRefused); errors during it arrive as an `error` event.
 */
export async function streamAi(
  path: string,
  body: unknown,
  onEvent: (e: AiStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
    signal,
  });
  if (!(res.headers.get('content-type') ?? '').includes('event-stream')) {
    const j = (await res.json().catch(() => null)) as { message?: string | string[] } | null;
    throw new AiRefused(
      Array.isArray(j?.message) ? j.message.join(', ') : (j?.message ?? `HTTP ${res.status}`),
    );
  }
  const reader = res.body!.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const block = buf.slice(0, i);
      buf = buf.slice(i + 2);
      if (block.startsWith('data: ')) onEvent(JSON.parse(block.slice(6)) as AiStreamEvent);
    }
  }
}
